import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuthElemStyle, isEmail, isPassword, setFocus } from '../../../../common/tools';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ISuccessfulAuth } from '../../../../interfaces';
import { MainContext } from '../../../../context/main.context';
import { ArrowBackSvg, CrossedOutEyeSvg, EyeSvg } from '../../../../icons';
import { LOGIN_BY_EMAIL_ERROR } from '../../../../common/errorCodes';
import Header from '../../../header';
import Recaptcha from '../../recaptcha';
import Footer from '../../../footer';
import ManualController from '../../../../hoc/ManualController';
import { loginByEmail } from '../../../../stores/auth.store';
import RecoveryPassword from '../recovery-password';
import Registration from '../registration';
import AuthMain from '../../index';


export default function LoginByMail() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [ email , setEmail ] = useState<string>('');
    const [ password, setPassword ] = useState<string>('');
    const [ hidePassword, setHidePassword ] = useState<boolean>(true);
    const { manualRedirect } = ManualController();
    const [ emailErr , setEmailErr ] = useState<string>('');
    const [ passwordErr, setPasswordErr ] = useState<string>('');
    const [ isDisableBtn, setDisableBtn ] = useState<boolean>(true);
    const {
        showCaptcha,
        settings,
        modalContent,
        session,
        source,
        locale,
        project,
        setModalContent,
        failedAttemptCenter,
    } = useContext(MainContext);

    useEffect(() => {
        if (email && password && !showCaptcha) {
            setDisableBtn(false);
        } else {
            setDisableBtn(true);
        }
    }, [ email, password, showCaptcha ]);

    const _navigate = (path: string): void => {
        if (session) {
            navigate({
                pathname: path,
                search: `?session=${session}&source=${source}&locale=${locale}&project=${project}`,
            });
        } else {
            navigate(path);
        }
    }

    const _onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();

        if (!email) {
            setFocus('log-email-id');
            setEmailErr(t('login.enterEmail'));
            return;
        }

        if (!isEmail(email)) {
            setFocus('log-email-id');
            setEmailErr(t('login.invalidEmail'));
            return;
        }

        if (emailErr) {
            setEmailErr('');
        }

        if (!password) {
            setFocus('log-password-id');
            setPasswordErr(t('login.enterPassword'));
            return;
        }

        if (!isPassword(password)) {
            setFocus('log-password-id');
            setPasswordErr(t('login.passwordValidation'));
            return;
        }

        if (passwordErr) {
            setPasswordErr('');
        }

        _loginByEmail();
    }

    const _failedAttemptCenter = (): void => {
        if (failedAttemptCenter) {
            failedAttemptCenter('add');
        }
    }

    const _loginByEmail = (): void => {
        loginByEmail(email, password).then((res: { data: ISuccessfulAuth }): void => {
            const warningMessage: string|undefined = res?.data?.warningMessage;

            if (warningMessage) {
                _failedAttemptCenter();

                if (warningMessage === 'USER_NOT_EXIST_LOGIN_BY_EMAIL') {
                    toast.warning(t('login.userNotExists'));

                    setEmail('');
                    setPassword('');
                } else if (warningMessage === 'INVALID_PASSWORD_LOGIN_BY_EMAIL') {
                    toast.warning(t('login.invalidPassword'));

                    setPassword('');
                }

                return;
            }

            const result: string|undefined = res?.data?.result;

            if (result) {
                manualRedirect(result, 'auth');
                return;
            }

            manualRedirect(res, 'auth', true, LOGIN_BY_EMAIL_ERROR);
        }, (err: any) => {
            const errData = err?.response?.data || err;

            manualRedirect(errData, 'auth', true, LOGIN_BY_EMAIL_ERROR);
        });
    }

    const _renderMenu = () => {
        return (
            <div className="auth-modal-menu">
                <div className="login-modal-header">
                    <div
                        className="login-modal-header-prev"
                        onClick={() => {
                            if (modalContent) {
                                setModalContent({ component: <AuthMain/> });
                                return;
                            }

                            _navigate('/session/login');
                        }}
                    >
                        <ArrowBackSvg/>
                        {t('common.back')}
                    </div>
                    <div className="login-modal-header-title text-overflow">
                        {t('login.auth')}
                    </div>
                </div>
                <form
                    noValidate
                    className="auth-modal-menu-body req-auth-modal-menu-body"
                    onSubmit={(e) => _onSubmit(e)}
                >
                    <div className="login-modal-menu-body-input">
                        <div className="login-modal-menu-body-input-title">
                            {t('login.emailLabel')}
                        </div>
                        <input
                            className="login-modal-menu-body-input-item"
                            type="email"
                            id="log-email-id"
                            autoComplete="off"
                            value={email}
                            autoFocus
                            onChange={(e) => setEmail(e.target.value)}
                            style={{border: `1px solid ${emailErr ? '#B01E1E' : '#d1d1d0'}`}}
                        >
                        </input>
                        { emailErr ? <div className="req-input-err">{emailErr}</div> : null }
                    </div>
                    <div className="login-modal-menu-body-input">
                        <div className="login-modal-menu-body-input-title">
                            {t('login.passwordLabel')}
                        </div>
                        <div className="login-modal-menu-body-input-password">
                            <input
                                className="login-modal-menu-body-input-item login-password"
                                id="log-password-id"
                                type={hidePassword ? 'password' : 'text'}
                                autoComplete="current-password"
                                title={t('login.passwordValidation')}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                style={{border: `1px solid ${passwordErr ? '#B01E1E' : '#d1d1d0'}`}}
                            >
                            </input>
                            <div className="password-hide" onClick={() => setHidePassword(!hidePassword)}>
                                { hidePassword ? <EyeSvg/> : <CrossedOutEyeSvg/> }
                            </div>
                            { passwordErr ? <div className="req-input-err">{passwordErr}</div> : null }
                        </div>
                    </div>
                    <button
                        className="btn"
                        style={{
                            background: isDisableBtn ? '#D1D1D0' : '#2368a2',
                            cursor: isDisableBtn ? 'auto' : 'pointer',
                        }}
                        type="submit"
                        disabled={isDisableBtn}
                    >
                        {t('login.loginButton')}
                    </button>
                    <div className="auth-modal-menu-footer req-auth-modal-menu-footer">
                        <div className="auth-modal-menu-footer-no-acc text-overflow">
                            {t('login.forgotPassword')}?
                        </div>
                        <div
                            className="auth-modal-menu-footer-reg text-overflow"
                            onClick={() => {
                                if (modalContent) {
                                    setModalContent({ component: <RecoveryPassword/> });
                                    return;
                                }

                                _navigate('/auth/recovery-password');
                            }}
                        >
                            {t('login.restore')}
                        </div>
                    </div>
                    <div className="auth-modal-menu-footer req-auth-modal-menu-footer">
                        <div className="auth-modal-menu-footer-no-acc text-overflow">
                            {t('login.noAcc')}?
                        </div>
                        <div
                            className="auth-modal-menu-footer-reg text-overflow"
                            onClick={() => {
                                if (modalContent) {
                                    setModalContent({ component: <Registration/> });
                                    return;
                                }

                                _navigate('/session/registration');
                            }}
                        >
                            {t('login.reg')}
                        </div>
                    </div>
                </form>
                <Recaptcha/>
            </div>
        );
    }

    return (
        <div className="auth" id="auth-element-id" style={{background: !modalContent ? '#e8e8e8' : 'unset'}}>
            <div
                className="auth-container"
                style={{
                    background: !modalContent ? '#e8e8e8' : 'unset',
                    minHeight: !modalContent ? 'calc(100vh - 100px)' : '100%',
                }}
            >
                <div className="auth-modal" style={getAuthElemStyle(settings)}>
                    <Header origin="auth"/>
                    {_renderMenu()}
                </div>
            </div>
            {!modalContent && <Footer/>}
        </div>
    );
}